/**
 * eSanza Super Admin — Shared Types
 * Derived from actual backend API response shapes in superAdminController.js
 * and schema definitions in backend/src/services/tenantSchema.js
 */

// ─── Tenant / Organisation ───────────────────────────────────────────────────

export interface TenantFeature {
  feature_key: string;
  label: string;
  category: string;
  is_enabled: boolean;
  config?: Record<string, string>;
  updated_at?: string;
}

export interface TenantPage {
  page_key: string;
  title: string;
  is_enabled: boolean;
  sort_order: number;
  config?: Record<string, string>;
  updated_at?: string;
}

export interface TenantCounts {
  users: number;
  farmers: number;
  cohorts: number;
  memberships?: number;
}

/** Lightweight list row — returned by GET /api/super-admin/tenants */
export interface TenantRow {
  id: number;
  name: string;
  slug: string;
  legal_name?: string;
  domain?: string;
  admin_domain?: string;
  contact_email?: string;
  contact_phone?: string;
  country?: string;
  default_currency?: string;
  currency_symbol?: string;
  status: 'active' | 'suspended' | 'archived' | 'setup' | string;
  marketplace_enabled: boolean;
  marketplace_access_status?: string;
  marketplace_participation_type?: 'buyer' | 'supplier' | 'buyer_and_supplier' | null;
  created_at: string;
  updated_at?: string;
  counts?: TenantCounts;
}

/** Full detail row — returned by GET /api/super-admin/tenants/:id */
export interface TenantDetail extends TenantRow {
  features: TenantFeature[];
  pages: TenantPage[];
  registration_requests?: RegistrationRequest[];
  settings?: Record<string, string>;
}

// ─── Registration Requests ───────────────────────────────────────────────────

export interface RegistrationRequest {
  id: number;
  invite_token: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | string;
  company_name?: string;
  legal_name?: string;
  tin?: string;
  registration_number?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  country?: string;
  province?: string;
  district?: string;
  sector?: string;
  cell?: string;
  village?: string;
  operating_districts?: string;
  business_type?: string;
  main_crops?: string;
  farmer_count?: number;
  website_url?: string;
  domain?: string;
  admin_domain?: string;
  requested_modules?: string[];
  notes?: string;
  submitted_at?: string;
  created_at: string;
  tenant_id?: number;
}

// ─── Users ───────────────────────────────────────────────────────────────────

export interface UserMembership {
  membership_id?: number;
  tenant_id: number;
  tenant_name: string;
  tenant_slug?: string;
  tenant_status?: string;
  role: string;
  status: 'active' | 'inactive' | 'pending' | string;
  invited_at?: string;
  accepted_at?: string;
  last_active_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface UserFarmerProfile {
  id: number;
  farmer_code?: string;
  full_name?: string;
  phone?: string;
  tenant_id: number;
  tenant_name?: string;
  is_active?: boolean;
  created_at?: string;
}

export interface UserPageAccess {
  tenant_id: number;
  tenant_name: string;
  pages: string[];
}

/** Lightweight list row — returned by GET /api/super-admin/users */
export interface UserRow {
  id: number;
  full_name: string;
  email: string;
  phone?: string;
  role: string;
  global_role?: string;
  is_active: boolean;
  registration_status: 'pending' | 'active' | 'inactive' | string;
  last_login?: string;
  created_at: string;
  invited_by_name?: string;
  tenant_id?: number;
  tenant_name?: string;
  workspace_count?: number;
  memberships?: UserMembership[];
}

/** Full detail — returned by GET /api/super-admin/users/:id */
export interface UserDetail extends UserRow {
  memberships: UserMembership[];
  farmer_profiles?: UserFarmerProfile[];
  page_access_by_tenant?: UserPageAccess[];
  updated_at?: string;
}

/** User row returned by GET /api/super-admin/tenants/:id/users */
export interface TenantUserRow {
  id: number;
  full_name: string;
  email?: string;
  phone?: string;
  role: string;
  membership_status: 'active' | 'inactive' | 'pending' | string;
  is_active: boolean;
  registration_status?: string;
  last_login?: string;
  created_at: string;
  farmer_id?: number;
  farmer_code?: string;
}

// ─── Platform Overview ────────────────────────────────────────────────────────

export interface UserRoleStat {
  role: string;
  count: number;
  active: number;
}

export interface PlatformOverview {
  users: UserRoleStat[];
  pendingApprovals: number;
  auditEventsToday: number;
  donations: { total: number; count: number };
}

// ─── Audit Logs ───────────────────────────────────────────────────────────────

export interface AuditLog {
  id: number;
  actor_id: number;
  actor_name?: string;
  actor_email?: string;
  actor_role: string;
  action: string;
  module: string;
  entity_type?: string;
  entity_id?: string;
  old_values?: Record<string, unknown>;
  new_values?: Record<string, unknown>;
  ip_address?: string;
  http_method?: string;
  http_path?: string;
  http_status?: number;
  created_at: string;
}

// ─── Roles ────────────────────────────────────────────────────────────────────

export interface RoleDefinition {
  role: string;
  permissions: string[];
  permissionCount: number;
}

// ─── Paginated Response ───────────────────────────────────────────────────────

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// ─── API Error ────────────────────────────────────────────────────────────────

export interface ApiError {
  error: string;
  message?: string;
}

// ─── Health ───────────────────────────────────────────────────────────────────

export interface HealthStatus {
  status: 'healthy' | 'unhealthy';
  timestamp: string;
  database: 'connected' | 'disconnected';
  service: string;
  version?: Record<string, string>;
}

// ─── Feature category groupings ──────────────────────────────────────────────
// Matches backend/src/services/tenantSchema.js DEFAULT_FEATURES categories

export const FEATURE_CATEGORIES: Record<string, string[]> = {
  'Core': ['dashboard'],
  'Operations': ['farmers', 'cohorts', 'crops', 'traceability', 'activities', 'field_diary', 'issues', 'training', 'compost'],
  'Finance': ['vsla'],
  'Commercial': ['warehouse', 'sales', 'orders', 'marketplace'],
  'Spatial': ['maps'],
  'Mobile': ['mobile_field_scouting'],
  'Content': ['website_control'],
  'Impact': ['aaywa', 'donations'],
  'Governance': ['funding_readiness'],
};

// ─── Lifecycle status config ──────────────────────────────────────────────────
// Uses forest/emerald for active, blue for setup, amber for suspended, neutral for archived

export const TENANT_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; borderColor: string }> = {
  active: { label: 'Active', color: 'text-emerald-700', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
  suspended: { label: 'Suspended', color: 'text-amber-700', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
  archived: { label: 'Archived', color: 'text-slate-600', bgColor: 'bg-slate-100', borderColor: 'border-slate-200' },
  setup: { label: 'Setup', color: 'text-blue-700', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
};

export const USER_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  active: { label: 'Active', color: 'text-emerald-700', bgColor: 'bg-emerald-50' },
  inactive: { label: 'Inactive', color: 'text-red-700', bgColor: 'bg-red-50' },
  pending: { label: 'Pending', color: 'text-amber-700', bgColor: 'bg-amber-50' },
  suspended: { label: 'Suspended', color: 'text-orange-700', bgColor: 'bg-orange-50' },
};

/**
 * Role color config — uses semantic neutral/blue tones per brief.
 * No purple. Super Admin uses red to signal elevated platform access.
 * Project Manager uses blue for management authority.
 * Others use emerald/amber/slate for field and operational roles.
 */
export const ROLE_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  super_admin: { label: 'Super Admin', color: 'text-red-700', bgColor: 'bg-red-50' },
  project_manager: { label: 'Project Manager', color: 'text-blue-700', bgColor: 'bg-blue-50' },
  agronomist: { label: 'Agronomist', color: 'text-emerald-700', bgColor: 'bg-emerald-50' },
  field_facilitator: { label: 'Field Facilitator', color: 'text-amber-700', bgColor: 'bg-amber-50' },
  farmer: { label: 'Farmer', color: 'text-slate-600', bgColor: 'bg-slate-100' },
};

export const TENANT_ROLE_OPTIONS = ['project_manager', 'agronomist', 'field_facilitator', 'farmer'] as const;
export type TenantRole = typeof TENANT_ROLE_OPTIONS[number];

// ─── Default Feature/Page definitions ────────────────────────────────────────
// Mirrored from backend/src/services/tenantSchema.js

export interface FeatureDefinition {
  key: string;
  label: string;
  category: string;
  enabled: boolean;
}

export interface PageDefinition {
  key: string;
  title: string;
  enabled: boolean;
  order: number;
}

export const DEFAULT_FEATURES: FeatureDefinition[] = [
  { key: 'dashboard', label: 'Dashboard', category: 'Core', enabled: true },
  { key: 'farmers', label: 'Farmers', category: 'Operations', enabled: true },
  { key: 'cohorts', label: 'Cohorts', category: 'Operations', enabled: true },
  { key: 'crops', label: 'Crops', category: 'Operations', enabled: true },
  { key: 'traceability', label: 'Traceability', category: 'Operations', enabled: true },
  { key: 'activities', label: 'Activities', category: 'Operations', enabled: true },
  { key: 'field_diary', label: 'Field Diary', category: 'Operations', enabled: true },
  { key: 'issues', label: 'Field Issues', category: 'Operations', enabled: true },
  { key: 'training', label: 'Training', category: 'Operations', enabled: true },
  { key: 'vsla', label: 'VSLA Finance', category: 'Finance', enabled: true },
  { key: 'warehouse', label: 'Warehouse', category: 'Commercial', enabled: true },
  { key: 'sales', label: 'Inputs & Sales', category: 'Commercial', enabled: true },
  { key: 'orders', label: 'Orders', category: 'Commercial', enabled: true },
  { key: 'compost', label: 'Compost & Inputs', category: 'Operations', enabled: true },
  { key: 'marketplace', label: 'Shared Marketplace', category: 'Commercial', enabled: false },
  { key: 'maps', label: 'Farm Maps', category: 'Spatial', enabled: true },
  { key: 'mobile_field_scouting', label: 'Mobile Field Scouting', category: 'Mobile', enabled: true },
  { key: 'website_control', label: 'Public Website', category: 'Content', enabled: true },
  { key: 'aaywa', label: 'AAYWA Program', category: 'Impact', enabled: false },
  { key: 'donations', label: 'Donations', category: 'Impact', enabled: false },
  { key: 'funding_readiness', label: 'Funding Readiness', category: 'Governance', enabled: true },
];

export const DEFAULT_PAGES: PageDefinition[] = [
  { key: 'dashboard', title: 'Dashboard', enabled: true, order: 10 },
  { key: 'farmers', title: 'Farmers', enabled: true, order: 20 },
  { key: 'users', title: 'System Users', enabled: true, order: 30 },
  { key: 'website-control', title: 'Public Website', enabled: true, order: 40 },
  { key: 'cohorts', title: 'Cohorts', enabled: true, order: 50 },
  { key: 'vsla', title: 'VSLA Groups', enabled: true, order: 60 },
  { key: 'crops', title: 'Crops', enabled: true, order: 70 },
  { key: 'traceability', title: 'Traceability', enabled: true, order: 80 },
  { key: 'activities', title: 'Activities', enabled: true, order: 90 },
  { key: 'field-diary', title: 'Field Diary', enabled: true, order: 95 },
  { key: 'inputs-sales', title: 'Inputs & Sales', enabled: true, order: 100 },
  { key: 'orders', title: 'Orders', enabled: true, order: 110 },
  { key: 'compost', title: 'Compost & Inputs', enabled: true, order: 120 },
  { key: 'training', title: 'Training', enabled: true, order: 130 },
  { key: 'issues', title: 'Issues', enabled: true, order: 140 },
  { key: 'warehouse', title: 'Warehouse', enabled: true, order: 150 },
  { key: 'maps', title: 'Farm Maps', enabled: true, order: 160 },
  { key: 'donations', title: 'Donations', enabled: false, order: 170 },
  { key: 'funding-readiness', title: 'Funding Readiness', enabled: true, order: 180 },
  { key: 'audit-logs', title: 'Audit Logs', enabled: true, order: 190 },
];
